import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';
import { PageHead } from 'views/design-system';

import { Empty, RuleChips, ScoreCell, StatusChip, TradeAmountCell } from '../bits';
import { LineChart } from '../charts';
import { ago, dateLine, hm, hms, money, usd } from '../data/format';
import { ITransaction, severityOf } from '../data/types';
import { useCases } from '../data/use-tm';
import { TxnDrawer } from '../triage/txn-drawer';
import { CaseDrawer } from './case-drawer';
import { RelationshipGraph } from './relationship-graph';

import '../../design-system/tokens.scss';
import '../../design-system/surface.scss';
import '../txn-surface.scss';
import './investigate-deck.scss';

interface ISubjectRow {
	vendorData: string;
	name: string;
	entityType: string;
	country: string;
	transactions: number;
	cumulativeRisk: number;
	worstScore: number;
	volume: number;
	declined: number;
	inReview: number;
	openAlerts: number;
	lastSeen: string;
}

interface ISubjectDetail {
	vendorData: string;
	name: string;
	entityType: string;
	country: string;
	series: { date: string; worstScore: number; transactions: number }[];
	counterparties: {
		name: string;
		country: string | null;
		entityType: string;
		transactions: number;
		volume: number;
		flagged: number;
	}[];
	sharedWith: string[];
	fingerprints: string[];
	transactions: ITransaction[];
}

const RANKS: Record<string, string> = {
	risk: 'Cumulative risk',
	worst: 'Worst score',
	volume: 'Volume',
	open: 'Open alerts',
};

/**
 * The standard closing reasons.
 *
 * `outcome` is a free string in the model, so this list is a convention rather
 * than a constraint — but a fixed set is the point: an outcome typed freehand
 * cannot be counted, and "how many cases closed as false positives this quarter"
 * is the question this field exists to answer.
 */
const CASE_OUTCOMES = [
	'No further action',
	'False positive',
	'SAR filed — customer retained',
	'SAR filed — customer exited',
	'Customer exited',
	'Referred to law enforcement',
	'Duplicate of another case',
];

export const InvestigateDeck: FC = () => {
	const { get } = useNetwork({ updateState: false });
	const [subjects, setSubjects] = useState<ISubjectRow[]>([]);
	const [selected, setSelected] = useState<string | null>(null);
	const [detail, setDetail] = useState<ISubjectDetail | null>(null);
	const [rank, setRank] = useState('risk');
	const [q, setQ] = useState('');
	/**
	 * The subjects a TRANSACTION-ID search resolves to.
	 *
	 * The subjects list is keyed by party, so a transaction id matches nothing in
	 * it by construction — an analyst holding a txnId from a report or a ticket
	 * had no way to ask this screen who it belonged to. The id is resolved
	 * server-side to the subject who owns it, and that subject is then shown.
	 *
	 * Kept separate from the local name filter rather than replacing it: name and
	 * vendorData are already in memory and filter instantly, and making every
	 * keystroke wait on a round trip to search text we already hold would be a
	 * worse screen, not a more capable one.
	 */
	const [txnSubjects, setTxnSubjects] = useState<string[]>([]);
	const [txnSearching, setTxnSearching] = useState(false);
	const [openTxn, setOpenTxn] = useState<string | null>(null);
	/* The case drawer and the transaction drawer occupy the same edge of the
	   screen, so opening either closes the other. */
	const [openCaseId, setOpenCaseId] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	/* "With open alerts" is a client-side narrowing of a list the page already
	   holds, not another request — the subjects endpoint returns openAlerts on
	   every row for exactly this. */
	const [openOnly, setOpenOnly] = useState(false);
	/* Clicking a graph node narrows the evidence to what passed through it. Held
	   here rather than in the graph so the table and the graph cannot disagree
	   about what is selected. */
	const [node, setNode] = useState<string | null>(null);
	const [narrative, setNarrative] = useState('');
	const [caseNote, setCaseNote] = useState('');
	/* The closing reason. Held here rather than sent straight from the picker
	   because closing is one request carrying BOTH the outcome and the status —
	   the service refuses `status: 'CLOSED'` on a case with no outcome, so
	   sending them separately makes the first of the two fail. */
	const [outcome, setOutcome] = useState('');

	const loadSubjects = useCallback(async () => {
		const resp = await get(`${API_URL.TM_SUBJECTS}?days=30`).catch(() => null);
		setSubjects(Array.isArray(resp?.data) ? resp.data : []);
		setLoading(false);
	}, [get]);

	useEffect(() => {
		loadSubjects();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* The first subject is selected automatically, but only until the analyst
	   picks one — re-selecting on every poll would drag them off the row they
	   are reading. */
	useEffect(() => {
		if (!selected && subjects[0]) setSelected(subjects[0].vendorData);
	}, [subjects, selected]);

	useEffect(() => {
		if (!selected) return;
		let live = true;
		get(`${API_URL.TM_SUBJECTS}/${selected}?days=30`)
			.then(resp => {
				if (live && resp?.data) setDetail(resp.data);
			})
			.catch(() => undefined);
		return () => {
			live = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected]);

	/* A node filter belongs to the subject it was chosen on — leaving it set
	   while switching subjects filters the new evidence by a counterparty that
	   may not appear in it at all, which reads as an empty table. */
	useEffect(() => {
		setNode(null);
		setNarrative('');
		setOutcome('');
	}, [selected]);

	/* DEBOUNCED, and it abandons its own result if the needle moved on: without
	   the generation check a slow response for "TXN-1" can land after a fast one
	   for "TXN-12" and repopulate the list with the wrong subject. */
	useEffect(() => {
		const needle = q.trim();
		if (needle.length < 3) {
			setTxnSubjects([]);
			setTxnSearching(false);
			return;
		}
		let live = true;
		setTxnSearching(true);
		const t = setTimeout(() => {
			get(`${API_URL.TM_TRANSACTIONS}?pageSize=50&q=${encodeURIComponent(needle)}`)
				.then(resp => {
					if (!live) return;
					const rows: { subjectVendorData?: string }[] = resp?.data?.data ?? [];
					/* Array.from rather than spreading the Set: this tsconfig targets
					   ES5 without downlevelIteration, where spreading an iterator
					   does not compile. */
					setTxnSubjects(
						Array.from(
							new Set(rows.map(r => r.subjectVendorData).filter(Boolean) as string[])
						)
					);
				})
				.catch(() => live && setTxnSubjects([]))
				.finally(() => live && setTxnSearching(false));
		}, 350);
		return () => {
			live = false;
			clearTimeout(t);
		};
	}, [q, get]);

	const ranked = useMemo(() => {
		const needle = q.trim().toLowerCase();
		const rows = needle
			? subjects.filter(
					s =>
						s.name.toLowerCase().includes(needle) ||
						s.vendorData.toLowerCase().includes(needle) ||
						/* …or this subject owns a transaction the id search matched. */
						txnSubjects.includes(s.vendorData)
				)
			: subjects.slice();
		const scoped = openOnly ? rows.filter(s => s.openAlerts > 0) : rows;
		/* Not a Record lookup: under `noUncheckedIndexedAccess` every read off one
		   is `fn | undefined`, so the comparator has to be narrowed before use.
		   Cumulative risk is the default because it is what the screen claims to
		   rank by in its own subheading. */
		const by = (s: ISubjectRow): number =>
			rank === 'worst'
				? s.worstScore
				: rank === 'volume'
					? s.volume
					: rank === 'open'
						? s.openAlerts
						: s.cumulativeRisk;
		return scoped.sort((a, b) => by(b) - by(a));
	}, [subjects, q, rank, openOnly, txnSubjects]);

	/**
	 * KEEP THE SELECTION INSIDE THE FILTERED LIST.
	 *
	 * The graph and the case panel render the SELECTED subject, and filtering the
	 * list never touched the selection — so searching a transaction id showed the
	 * right subject in the list beside the previously-selected subject's graph,
	 * which is worse than not searching at all: every panel on screen except one
	 * describes a party the analyst did not ask about.
	 *
	 * Only re-selects when the current selection has been filtered OUT. A
	 * selection that is still visible is left alone — the analyst may be narrowing
	 * the list around a subject they are already reading, and yanking the panels
	 * to the top row on every keystroke would fight them.
	 */
	useEffect(() => {
		if (!ranked.length) return;
		if (selected && ranked.some(s => s.vendorData === selected)) return;
		setSelected(ranked[0]?.vendorData ?? null);
	}, [ranked, selected]);

	const series = detail?.series ?? [];
	const lastSeen = detail?.transactions?.[0]?.txnDate ?? null;

	const { cases, openCase, updateCase } = useCases(selected);
	/* One case is "the" case for this subject: the newest that is still open.
	   A subject with a filed SAR and a fresh alert has two, and the panel should
	   be working the live one. */
	const active = cases.find(c => c.status !== 'CLOSED' && c.status !== 'SAR_FILED') ?? cases[0];

	const evidence = useMemo(() => {
		const rows = detail?.transactions ?? [];
		return node ? rows.filter(t => t.counterpartyName === node) : rows;
	}, [detail, node]);

	const pinned: string[] = (active?.pinnedTransactionIds ?? []).map((x: any) => String(x));

	return (
		<div className="sv2-scope sv2 txn-deck">
			<PageHead
				title="Txn Monitoring — Subject Investigate"
				sub={
					<>
						{dateLine()} · Didit KYT · subjects ranked by cumulative risk · the relationships are what the
						rules saw, the transactions are the evidence
					</>
				}
			/>

			<div className="ctl">
				<span className="range">Last 30 days</span>
				<button
					className="pre"
					type="button"
					onClick={() => {
						const keys = Object.keys(RANKS);
						setRank(k => keys[(keys.indexOf(k) + 1) % keys.length] ?? 'risk');
					}}
				>
					Ranked by: <b>{RANKS[rank]}</b> ▾
				</button>
				<button
					className={`pre${openOnly ? ' on' : ''}`}
					type="button"
					onClick={() => setOpenOnly(v => !v)}
				>
					With open alerts
				</button>
				<span className="rn">
					{ranked.length} of {subjects.length} subjects in the window
				</span>
			</div>

			<div className="strip estrip">
				<div className="tile">
					<div className="l"><span className="mk mk2" />Subjects</div>
					<div className="n">{subjects.length}</div>
					<div className="d">active in the last 30 days</div>
				</div>
				<div className="tile">
					<div className="l"><span className="mk mk5" />With open alerts</div>
					<div className="n">{subjects.filter(s => s.openAlerts > 0).length}</div>
					<div className="d">still need a decision</div>
				</div>
				<div className="tile">
					<div className="l"><span className="mk mk3" />Declined</div>
					<div className="n">{subjects.reduce((a, s) => a + s.declined, 0)}</div>
					<div className="d">transactions, all subjects</div>
				</div>
				<div className="tile">
					<div className="l"><span className="mk mk1" />Volume</div>
					<div className="n">{usd(subjects.reduce((a, s) => a + s.volume, 0))}</div>
					<div className="d">monitored in the window</div>
				</div>
			</div>

			{/* SEARCH SITS HERE, not up in the control strip where it started.
			    It reads as a filter over the three panels directly beneath it —
			    which is what it is — and the strip above is about how the list is
			    RANKED, a different question. Moved rather than duplicated: two
			    search boxes on one screen, doing subtly different things, is worse
			    than one that does the whole job. */}
			<div className="decksearch">
				<label className="srch" title="Searches subject name, our vendor id, and transaction IDs">
					<svg viewBox="0 0 24 24">
						<circle cx="11" cy="11" r="7" />
						<line x1="16.5" y1="16.5" x2="21" y2="21" />
					</svg>
					<input
						placeholder="Search by name or transaction ID"
						value={q}
						onChange={e => setQ(e.target.value)}
					/>
				</label>
				{q.trim() ? (
					<span className="hint">
						{txnSearching
							? 'searching transactions…'
							: txnSubjects.length
								? `matched ${txnSubjects.length} subject${
										txnSubjects.length === 1 ? '' : 's'
									} by transaction`
								: null}
						{q.trim() ? (
							<button className="clr" type="button" onClick={() => setQ('')}>
								clear
							</button>
						) : null}
					</span>
				) : null}
			</div>

			<div className="inv">
				<div>
					<div className="panel">
						<div className="ph">
							Subjects <span className="r">{ranked.length}</span>
						</div>
						{ranked.length === 0 && (
							<Empty>
								{loading
									? 'Loading subjects…'
									: q.trim()
										? /* SAY WHICH IS EMPTY: the search or the window. Reporting
										     "No transactions in the last 30 days" to someone who has
										     just typed a name blames the data for what is actually a
										     miss on their needle — and reads as though the platform
										     lost their transactions. */
											`Nothing matches “${q.trim()}”. Searched subject names, vendor ids and transaction IDs in the last 30 days.`
										: subjects.length
											? 'No subjects match the current filters.'
											: 'No transactions in the last 30 days.'}
							</Empty>
						)}
						{ranked.map((s, i) => (
							<div
								key={s.vendorData}
								className={`subj${selected === s.vendorData ? ' sel' : ''}`}
								onClick={() => setSelected(s.vendorData)}
								role="button"
								tabIndex={0}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') setSelected(s.vendorData);
								}}
							>
								<div className="rk">{i + 1}</div>
								<div>
									<div className="sn">{s.name}</div>
									<div className="sm">
										{s.vendorData} · {s.country} · {s.transactions} txns
									</div>
								</div>
								<div className={`sev ${severityOf(s.worstScore).toLowerCase()}`}>{s.worstScore}</div>
								<div className={s.openAlerts ? 'rd' : 'gy'}>{s.openAlerts}</div>
							</div>
						))}
					</div>
				</div>

				<div>
					<div className="panel">
						<div className="ph">
							Relationship graph
							<span className="r">
								{node ? `filtered to ${node}` : 'click a node to filter the evidence'}
							</span>
						</div>
						{detail ? (
							<RelationshipGraph
								subjectName={detail.name}
								subjectVendorData={detail.vendorData}
								counterparties={detail.counterparties}
								sharedWith={detail.sharedWith}
								selected={node}
								onSelect={setNode}
								/* Opening the other party is a subject change, so it goes
								   through the same setter the ranked list uses — which
								   already clears the node filter and the narrative draft
								   for the subject being left. */
								onOpenSubject={setSelected}
							/>
						) : (
							<Empty>Select a subject to see its relationships.</Empty>
						)}
					</div>

					<div className="panel">
						<div className="ph">
							Risk over time <span className="r">worst score per day · 30 days</span>
						</div>
						{series.length ? (
							<div className="chwrap">
								<LineChart
									series={[
										{
											name: 'worst score',
											vals: series.map(d => d.worstScore),
											color: 'var(--c2)',
											area: true,
										},
									]}
									height={150}
									xlabels={['30d ago', '', 'today']}
									tip={i => series[i]?.date ?? ''}
								/>
							</div>
						) : (
							<Empty>Select a subject to see its risk history.</Empty>
						)}
					</div>

					<div className="panel">
						<div className="ph">
							Counterparties <span className="r">who this subject transacts with</span>
						</div>
						<div className="tscroll">
							<table className="stbl">
								<thead>
									<tr>
										<th>Counterparty</th>
										<th>Country</th>
										<th className="num">Txns</th>
										<th className="num">Volume</th>
										<th className="num">Flagged</th>
									</tr>
								</thead>
								<tbody>
									{(detail?.counterparties ?? []).length === 0 && (
										<tr>
											<td colSpan={5} className="empty">
												Nothing for this subject in the window.
											</td>
										</tr>
									)}
									{(detail?.counterparties ?? [])
										.slice()
										.sort((a, b) => b.transactions - a.transactions)
										.map(c => (
											<tr key={c.name}>
												<td>{c.name}</td>
												<td className="mono">{c.country ?? 'unhosted'}</td>
												<td className="num">{c.transactions}</td>
												<td className="num">{usd(c.volume)}</td>
												<td className={`num ${c.flagged ? 'rd' : 'gy'}`}>{c.flagged}</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
					</div>

					<div className="panel">
						<div className="ph">
							Evidence · transactions{' '}
							<span className="r">
								{evidence.length}
								{node ? ` of ${detail?.transactions?.length ?? 0} · ${node}` : ''}
							</span>
						</div>
						<div className="tscroll">
							<table className="stbl ev">
								<thead>
									<tr>
										<th className="c-tm">Time</th>
										<th className="c-id">Txn</th>
										<th className="c-am num">Amount</th>
										<th className="c-cp">Counterparty</th>
										<th className="c-ru">Rules</th>
										<th className="c-sc">Score</th>
										<th className="c-st">Status</th>
										<th className="c-pin">Pin</th>
									</tr>
								</thead>
								<tbody>
									{evidence.length === 0 && (
										<tr>
											<td colSpan={8} className="empty">
												{node
													? `Nothing passed through ${node}.`
													: 'Select a subject to see its transactions.'}
											</td>
										</tr>
									)}
									{evidence.map(t => (
										<tr
											key={t._id}
											onClick={() => {
												setOpenCaseId(null);
												setOpenTxn(t._id);
											}}
										>
											<td className="mono">{hm(new Date(t.txnDate).getTime())}</td>
											<td className="mono">{t.txnId}</td>
											<td className="num">
												<TradeAmountCell txn={t} />
											</td>
											<td>{t.counterpartyName}</td>
											<td>
												<RuleChips runs={t.ruleRuns} max={2} />
											</td>
											<td>
												<ScoreCell score={t.score} />
											</td>
											<td>
												<StatusChip status={t.status} />
											</td>
											<td>
												{active ? (
													<button
														className="linky"
														type="button"
														title={
															pinned.includes(t._id)
																? 'Remove this transaction from the case'
																: 'Pin this transaction as evidence on the case'
														}
														onClick={e => {
															e.stopPropagation();
															updateCase(
																active._id,
																pinned.includes(t._id)
																	? { unpinTransactionId: t._id }
																	: { pinTransactionId: t._id }
															);
														}}
													>
														{pinned.includes(t._id) ? 'unpin' : 'pin'}
													</button>
												) : (
													<span className="gy">—</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					<div className="foot">
						A subject here is every transaction sharing a <code>vendorData</code>, aggregated over the
						window — not a stored party. The party of record lives in clients.api; duplicating it would
						leave two copies of a name to drift apart. Cumulative risk is the SUM of scores rather than
						the worst one: a party that trips a 30 twenty times is a different problem from one that
						tripped a 65 once, and ranking on the worst single score hides the first case entirely.{' '}
						Volume counts each transaction once at its REPORTABLE amount — for a trade row that is
						the greater of its two legs, not the sum of both, so converting $14,250 of BTC into
						$14,100 adds $14,250 to the subject&apos;s volume rather than $28,350.
						Fees are excluded: volume is value moved, not what the customer paid to move it.
					</div>
				</div>

				<div className="rside">
					<div className="panel">
						<div className="ph">
							Case <span className="r">{active ? active.caseNumber : 'none open'}</span>
						</div>
						{active ? (
							<>
								<table className="kv casekv">
									<tbody>
										<tr><td>number</td><td className="mono">{active.caseNumber}</td></tr>
										<tr><td>status</td><td>{active.status.replace('_', ' ').toLowerCase()}</td></tr>
										<tr><td>assigned</td><td>{active.assignedTo ?? '—'}</td></tr>
										<tr><td>alerts</td><td>{active.linkedAlertIds?.length ?? 0}</td></tr>
										<tr><td>pinned evidence</td><td>{pinned.length}</td></tr>
										{active.sar?.filedAt && (
											<tr>
												<td>SAR filed</td>
												<td className="g">{new Date(active.sar.filedAt).toLocaleString()}</td>
											</tr>
										)}
									</tbody>
								</table>
								<div className="casebtns">
									<button
										className="dbtn"
										type="button"
										data-qa="open-case-drawer"
										onClick={() => {
											setOpenTxn(null);
											setOpenCaseId(active._id);
										}}
									>
										SAR status &amp; case details →
									</button>
								</div>

								{/* The narrative is the SAR. It is a draft until filed, and filing is
								    refused without it — a filed report with no argument in it is the
								    one thing worse than an unfiled one. */}
								<div className="cmt">
									<textarea
										value={narrative || (active.sar?.narrative ?? '')}
										onChange={e => setNarrative(e.target.value)}
										disabled={!!active.sar?.filedAt}
										placeholder="SAR narrative — what happened, why it is suspicious, and what the pinned evidence shows"
									/>
									<div className="opts">
										<button
											className="dbtn"
											type="button"
											disabled={!!active.sar?.filedAt || !narrative.trim()}
											onClick={() => updateCase(active._id, { narrative: narrative.trim() })}
										>
											Save narrative
										</button>
										<button
											className="dbtn danger"
											type="button"
											style={{ marginLeft: 'auto' }}
											disabled={!!active.sar?.filedAt || !(narrative.trim() || active.sar?.narrative)}
											onClick={async () => {
												/* Save first, then file: filing reads the STORED narrative, so
												   filing an unsaved draft would file an empty one. */
												if (narrative.trim()) {
													await updateCase(active._id, { narrative: narrative.trim() });
												}
												await updateCase(active._id, { fileSar: true });
											}}
										>
											{active.sar?.filedAt ? 'SAR filed' : 'File SAR'}
										</button>
									</div>
								</div>

								<div className="cmt">
									<textarea
										value={caseNote}
										onChange={e => setCaseNote(e.target.value)}
										placeholder="Case note"
									/>
									<div className="opts">
										<button
											className="dbtn pri"
											type="button"
											style={{ marginLeft: 'auto' }}
											disabled={!caseNote.trim()}
											onClick={async () => {
												await updateCase(active._id, { note: caseNote.trim() });
												setCaseNote('');
											}}
										>
											Add note
										</button>
									</div>
								</div>

								{/* CLOSING THE CASE.
								    The service has enforced "no outcome, no close" since the entity
								    existed (`cases/index.ts`), and nothing on any screen ever sent
								    either field — so a case, once escalated, stayed open forever and
								    the CLOSED status in the model was unreachable. The outcome and the
								    status go in ONE request because the guard reads the outcome off
								    the document it is about to save.

								    A filed SAR does not close a case on its own: filing sets
								    `SAR_FILED`, and what happened next — exited, monitored, referred —
								    is a separate fact somebody has to record. */}
								{active.status !== 'CLOSED' ? (
									<div className="casebtns">
										<label className="csel">
											<span>Outcome</span>
											<select
												value={outcome}
												onChange={e => setOutcome(e.target.value)}
											>
												<option value="">Choose an outcome…</option>
												{CASE_OUTCOMES.map(o => (
													<option key={o} value={o}>
														{o}
													</option>
												))}
											</select>
										</label>
										<button
											className="dbtn danger"
											type="button"
											disabled={!outcome}
											title={
												outcome
													? `Close ${active.caseNumber} as "${outcome}"`
													: 'Choose an outcome first — a case cannot be closed without one'
											}
											onClick={async () => {
												await updateCase(active._id, {
													outcome,
													status: 'CLOSED',
												});
												setOutcome('');
											}}
										>
											Close case
										</button>
									</div>
								) : (
									<div className="case-closed">
										Closed · <b>{active.outcome ?? 'no outcome recorded'}</b>
									</div>
								)}

								{(active.notes ?? [])
									.slice()
									.reverse()
									.map((n: any) => (
										<div className="case-note" key={`${n.at}-${n.text}`}>
											{n.text}
											<span className="at">
												{n.who} · {hms(new Date(n.at).getTime())}
											</span>
										</div>
									))}
							</>
						) : (
							<>
								<Empty>
									No case open for this subject. Escalating an alert from triage opens one
									automatically; this button is for starting an investigation without one.
								</Empty>
								<div className="casebtns" style={{ padding: '0 12px 12px' }}>
									<button
										className="dbtn pri"
										type="button"
										disabled={!detail}
										onClick={() => detail && openCase(`${detail.name} · manual investigation`)}
									>
										Open a case
									</button>
								</div>
							</>
						)}
					</div>

					<div className="panel">
						<div className="ph">Subject</div>
						{detail ? (
							<table className="kv">
								<tbody>
									<tr><td>name</td><td>{detail.name}</td></tr>
									<tr><td>vendor data</td><td className="mono">{detail.vendorData}</td></tr>
									<tr><td>entity type</td><td>{detail.entityType}</td></tr>
									<tr><td>country</td><td>{detail.country}</td></tr>
									<tr>
										<td>devices</td>
										<td className="mono">{detail.fingerprints.length}</td>
									</tr>
								</tbody>
							</table>
						) : (
							<Empty>No subject selected.</Empty>
						)}
					</div>

					<div className="panel">
						<div className="ph">
							Shared devices <span className="r">multi-accounting</span>
						</div>
						{detail?.sharedWith?.length ? (
							<>
								<div className="amlhit">
									<b>Device shared with {detail.sharedWith.length} other subject
									{detail.sharedWith.length > 1 ? 's' : ''}</b> — this is what the
									<code> Multi-accounting </code> rule scores. The other parties on the same
									fingerprint are listed below.
								</div>
								<table className="kv">
									<tbody>
										{detail.sharedWith.map(v => (
											<tr key={v}>
												<td className="mono">{v}</td>
												<td>
													<button className="linky" type="button" onClick={() => setSelected(v)}>
														open
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</>
						) : (
							<Empty>No device of this subject was seen on another party.</Empty>
						)}
					</div>

					<div className="panel">
						<div className="ph">Activity</div>
						{detail?.transactions?.length ? (
							<table className="kv">
								<tbody>
									<tr>
										<td>last seen</td>
										<td>{lastSeen ? `${ago(new Date(lastSeen).getTime())} ago` : '—'}</td>
									</tr>
									<tr>
										<td>declined</td>
										<td className="rd">
											{detail.transactions.filter(t => t.status === 'DECLINED').length}
										</td>
									</tr>
									<tr>
										<td>in review</td>
										<td className="a">
											{detail.transactions.filter(t => t.status === 'IN_REVIEW').length}
										</td>
									</tr>
									<tr>
										<td>approved</td>
										<td className="g">
											{detail.transactions.filter(t => t.status === 'APPROVED').length}
										</td>
									</tr>
								</tbody>
							</table>
						) : (
							<Empty>Nothing to summarise yet.</Empty>
						)}
					</div>
				</div>
			</div>

			<TxnDrawer id={openTxn} onClose={() => setOpenTxn(null)} onChanged={loadSubjects} />
			<CaseDrawer
				kase={cases.find(c => c._id === openCaseId) ?? null}
				cases={cases}
				onPick={setOpenCaseId}
				onClose={() => setOpenCaseId(null)}
			/>
		</div>
	);
};

export default InvestigateDeck;
